import React, { FC, CSSProperties, useMemo } from 'react'
import { Box, Stack, useTheme, CircularProgress } from '@mui/material'
import { useState, useEffect } from 'react'
import { AnnotationOverlay } from './AnnotationOverlay'
import { AnnotationBox } from './NumberAnnotationPanel'

type Props = {
  imagesLength: number
  imageIndex: number
  imageUrl: string
  onPageIndicatorClick: () => void
  onImageClick: React.MouseEventHandler<HTMLImageElement>
  onLoad: React.ReactEventHandler<HTMLImageElement>
  imageStyle: CSSProperties
  styleForSmallScreen: CSSProperties
  containerAspectRatio: string
  // Annotation props
  isAnnotationMode?: boolean
  selectedField?: string
  annotations?: AnnotationBox[]
  onAnnotationAdd?: (annotation: AnnotationBox) => void
  fieldColors?: Record<string, string>
}

export const AnnotatablePrescriptionImageBox: FC<Props> = ({
  imagesLength,
  imageIndex,
  imageUrl,
  onPageIndicatorClick,
  onImageClick,
  onLoad,
  imageStyle,
  styleForSmallScreen,
  containerAspectRatio,
  isAnnotationMode = false,
  selectedField = '',
  annotations = [],
  onAnnotationAdd = () => {},
  fieldColors = {},
}) => {
  const theme = useTheme()
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const image = new Image()
    image.onload = () => {
      setLoading(false)
    }
    image.src = imageUrl
    // imageUrlが変わった時は再度loading状態にする
    return () => {
      setLoading(true)
    }
  }, [imageUrl])

  const PrescImage = useMemo(
    () => (
      <div
        style={{
          width: '100%',
          aspectRatio: containerAspectRatio,
          overflow: 'hidden',
          position: 'relative', // Important for annotation overlay positioning
        }}
      >
        <img
          src={imageUrl}
          style={imageStyle}
          onLoad={onLoad}
          onClick={isAnnotationMode ? undefined : onImageClick}
        />
        {isAnnotationMode && (
          <AnnotationOverlay
            selectedField={selectedField}
            annotations={annotations}
            onAnnotationAdd={onAnnotationAdd}
            fieldColors={fieldColors}
            isActive={true}
            imageContainerStyle={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          />
        )}
      </div>
    ),
    [
      imageUrl,
      imageStyle,
      isAnnotationMode,
      selectedField,
      annotations,
      fieldColors,
      containerAspectRatio,
      onLoad,
      onImageClick,
      onAnnotationAdd,
    ]
  )

  return (
    <Box
      sx={{
        [theme.breakpoints.down('md')]: styleForSmallScreen,
      }}
      width="50%"
    >
      <Stack
        sx={{
          [theme.breakpoints.up('md')]: {
            position: 'relative',
          },
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
        }}
        direction="row"
        bgcolor="#FFF"
        p={1}
      >
        {loading ? (
          <CircularProgress
            style={{
              color: theme.palette.secondary.dark,
              opacity: 0.3,
            }}
            size={80}
          />
        ) : (
          PrescImage
        )}
        <Box
          sx={{
            [theme.breakpoints.down('md')]: {
              right: 24,
            },
          }}
          position="absolute"
          top={8}
          right={24}
          bgcolor="#000"
          color="#fff"
          p={1}
          borderRadius="8px"
          style={{ opacity: 0.6 }}
          onClick={onPageIndicatorClick}
        >
          {`${imageIndex + 1}/${imagesLength}ページ中 ›`}
        </Box>
      </Stack>
    </Box>
  )
}
